// Enrichment in the app: PR5's reveals, PR7's reach, prices, and the actions that charge.
import {
  type EmailStatusValue,
  ROLE_FAMILY_DEFINITIONS,
  type RoleFamilyValue,
  formatEnrichCredits,
} from '@parcel/types';
import {
  type InfiniteData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiErrorCode, apiFetch } from './api.js';
import type {
  ApiAccountContactsResponse,
  ApiContactDetailResponse,
  ApiContactListResponse,
  ApiContactSummary,
} from './contacts.js';
import { chunkRecordIds } from './view-selection-state.js';

export type ApiEmailStatus = EmailStatusValue;
export type ApiRoleFamily = RoleFamilyValue;
export type ApiRevealKind = 'email' | 'phone' | 'profile';

/** PR5's unlocked values (`include=reveals`). */
export interface ApiContactReveals {
  email: {
    address: string;
    email_status: ApiEmailStatus | null;
    observed_at: string | null;
    revealed_at: string;
  } | null;
  phone: { number: string; observed_at: string | null; revealed_at: string } | null;
  profile: {
    employment_history: unknown;
    profile: unknown;
    /** The free profile facts beside the body; absent from a list read. */
    headline?: string | null;
    location?: string | null;
    about?: string | null;
    current_role?: {
      title: string | null;
      employer_name: string | null;
      started_on: string | null;
      description: string | null;
    } | null;
    observed_at: string | null;
    revealed_at: string;
  } | null;
}

/** PR7's reach block (`include=reach`); never a value. */
export interface ApiContactReach {
  email_status: ApiEmailStatus | null;
  available: {
    email: boolean;
    email_status: ApiEmailStatus | null;
    phone: boolean;
    profile: boolean;
  };
  revealed: ApiRevealKind[];
  persona: ApiRoleFamily | null;
  persona_match: boolean;
  sources: {
    filing_observed_at: string | null;
    provider_seen_at: string | null;
    role_started_on: string | null;
  };
  /** When a lookup of each kind last found nothing, within 30 days; an older API omits it. */
  missed?: { email: string | null; profile: string | null };
}

export interface ApiEnrichPrices {
  people_search_result: number | null;
  company_search_result: number | null;
  email_reveal: number | null;
  profile_reveal: number | null;
  phone_reveal: number | null;
  company_profile: number | null;
}

export interface ApiEnrichStatus {
  enabled: boolean;
  phone: boolean;
  prices: ApiEnrichPrices;
}

export interface ReachSource {
  email: string | null;
  phone: string | null;
  reveals?: ApiContactReveals;
  reach?: ApiContactReach;
}

export interface EmploymentRole {
  title: string | null;
  firm_name: string | null;
  location: string | null;
  start: string | null;
  end: string | null;
  description: string | null;
  contract_type: string | null;
  current: boolean;
  /** The firm's site when Parcel knows it by its LinkedIn id, for its logo. */
  firm_website: string | null;
  /** The firm's LinkedIn company id, when the profile named it. */
  firm_linkedin_id?: string | null;
}

export interface EducationEntry {
  school: string | null;
  degree: string | null;
  fields_of_study: string[];
  start: string | null;
  end: string | null;
}

export interface ContactProfileView {
  employment: EmploymentRole[];
  education: EducationEntry[];
  skills: string[];
  languages: Array<{ name: string; level: string | null }>;
  connections: number | null;
}

export type RevealResult =
  | { kind: 'found' }
  | { kind: 'miss' }
  | { kind: 'unavailable' }
  | { kind: 'insufficient_credits' }
  | { kind: 'spend_limit' };

export interface BatchRevealTally {
  found: number;
  missed: number;
  failed: number;
  creditsMicros: number;
  insufficient: boolean;
  spendLimited: boolean;
}

export const ENRICH_INSUFFICIENT_CREDITS = 'ENRICH_INSUFFICIENT_CREDITS';
export const ENRICH_SPEND_LIMIT_EXCEEDED = 'ENRICH_SPEND_LIMIT_EXCEEDED';

/** A 402 that refused for credits or the spend limit, as the kind the notice reads; else null. */
function creditRefusal(
  error: unknown,
): { kind: 'insufficient_credits' } | { kind: 'spend_limit' } | null {
  const code = apiErrorCode(error);
  if (code === ENRICH_INSUFFICIENT_CREDITS) return { kind: 'insufficient_credits' };
  if (code === ENRICH_SPEND_LIMIT_EXCEEDED) return { kind: 'spend_limit' };
  return null;
}
export const PEOPLE_SEARCH_LIMIT = 25;
/** A people search answers from the provider's last result for this long (the API's search cache). */
export const PEOPLE_SEARCH_FRESH_DAYS = 30;
/** A lookup that found nothing is not offered again for this long (the API's MISS_REMEMBERED_DAYS). */
export const LOOKUP_RETRY_DAYS = 30;
const DAY_MS = 86_400_000;

/** "Oct 16", with the year when it is not this year's. */
export function shortDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(date.getUTCFullYear() === now.getUTCFullYear() ? {} : { year: 'numeric' }),
  });
}

/** The day a window that opened at `iso` closes. */
export function windowEnd(iso: string, days: number): Date {
  return new Date(new Date(iso).getTime() + days * DAY_MS);
}
export const CONTACT_INCLUDES = ['reveals', 'reach'] as const;
export const ACCOUNT_CONTACT_INCLUDES = ['reveals', 'reach'] as const;
export const ACCOUNT_CONTACTS_LIMIT = 200;
const STATUS_STALE_MS = 5 * 60_000;
const BATCH_SIZE = 100;
const MICROS_PER_CREDIT = 1_000_000;

// -- Copy --------------------------------------------------------------------

/** The amount Spec states for the same charge, with the unit. */
export function formatCredits(microcredits: number): string {
  return `${formatEnrichCredits(microcredits)} credits`;
}

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

/** The search dialog's cost: the count and the total first, the unit price after. */
export function searchCostLine(unitMicrocredits: number, limit: number): string {
  const total = `up to ${formatCredits(unitMicrocredits * limit)}`;
  const each = limit > 1 ? ` (${formatEnrichCredits(unitMicrocredits)} each)` : '';
  return `Finds up to ${people(limit)} · ${total}${each}.`;
}

/** What a finished search found and charged; an API from before new_results states the charge alone. */
export function searchResultLine(result: {
  found: number;
  newCount: number | null;
  microcredits: number | null;
}): string {
  if (result.found === 0) return 'No new people found. You were not charged.';
  const head = `Found ${people(result.found)}`;
  if (result.newCount === 0) return `${head} · none new · not charged`;
  const fresh = result.newCount == null ? '' : ` · ${result.newCount} new`;
  const charged = result.microcredits == null ? '' : ` · ${formatCredits(result.microcredits)}`;
  return `${head}${fresh}${charged}`;
}

const PERSONA_LABELS = new Map<string, string>(
  ROLE_FAMILY_DEFINITIONS.map((definition) => [definition.value, definition.label]),
);

export function personaLabel(family: ApiRoleFamily): string {
  return PERSONA_LABELS.get(family) ?? family;
}

export function personasPhrase(families: readonly ApiRoleFamily[]): string {
  return families.map((family) => personaLabel(family).toLowerCase()).join(', ');
}

/** The tab reads one page; a full page means there are more, even with the open contact left out. */
export function contactsCountShown(count: number, pageRows = count): string {
  const shown = count.toLocaleString('en-US');
  return pageRows >= ACCOUNT_CONTACTS_LIMIT ? `${shown}+` : shown;
}

const MONTH_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Provider dates are free text at times; anything but an ISO date is shown as written. */
export function monthYear(iso: string): string {
  const date = /^\d{4}-\d{2}-\d{2}/.test(iso) ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? MONTH_YEAR.format(date) : iso;
}

export function sourceFreshnessLine(sources: ApiContactReach['sources']): string | null {
  const parts: string[] = [];
  if (sources.filing_observed_at) {
    parts.push(`Filing ${new Date(sources.filing_observed_at).getUTCFullYear()}`);
  }
  // Third-party data is Parcel's to the reader: a source is a filing or Parcel.
  if (sources.provider_seen_at) parts.push(`Parcel ${monthYear(sources.provider_seen_at)}`);
  if (sources.role_started_on) parts.push(`in role since ${monthYear(sources.role_started_on)}`);
  const line = parts.join(' · ');
  return line ? line.charAt(0).toUpperCase() + line.slice(1) : null;
}

export function shownEmail(contact: ReachSource): {
  value: string | null;
  status: ApiEmailStatus | null;
} {
  const revealed = contact.reveals?.email;
  if (revealed && revealed.email_status !== 'bounced') {
    return { value: revealed.address, status: revealed.email_status };
  }
  const status = contact.reach?.email_status ?? null;
  // A free address known to bounce is never shown, so a reveal is offered instead.
  return contact.email && status !== 'bounced'
    ? { value: contact.email, status }
    : { value: null, status: null };
}

export function shownPhone(contact: ReachSource): string | null {
  return contact.reveals?.phone?.number ?? contact.phone;
}

export function needsEmailReveal(contact: ReachSource): boolean {
  const reach = contact.reach;
  // A lookup that found nothing in the last 30 days is not sent again.
  const missed = Boolean(reach?.missed?.email) && !reach?.available.email;
  return !shownEmail(contact).value && !(reach?.revealed.includes('email') ?? false) && !missed;
}

// -- PR5 responses -------------------------------------------------------------

type Outcome = { status?: unknown; credits?: unknown; reason?: unknown };

function outcomeOf(record: unknown, kind: ApiRevealKind): Outcome {
  const outcomes = (record as { outcomes?: Record<string, unknown> } | null)?.outcomes;
  return (outcomes?.[kind] ?? {}) as Outcome;
}

export function readRevealResult(body: unknown, kind: ApiRevealKind): RevealResult {
  const { status } = outcomeOf((body as { data?: unknown } | null)?.data, kind);
  if (status === 'not_found') return { kind: 'miss' };
  if (status === 'unavailable' || status === 'error') return { kind: 'unavailable' };
  return { kind: 'found' };
}

function emptyTally(): BatchRevealTally {
  return {
    found: 0,
    missed: 0,
    failed: 0,
    creditsMicros: 0,
    insufficient: false,
    spendLimited: false,
  };
}

export function tallyBatchReveal(body: unknown): BatchRevealTally {
  const tally = emptyTally();
  const results = (body as { data?: { results?: unknown } } | null)?.data?.results;
  if (!Array.isArray(results)) return tally;
  for (const raw of results) {
    const item = raw as { status?: unknown; record?: unknown; error?: { code?: unknown } };
    if (item.status === 'ok') {
      const outcome = outcomeOf(item.record, 'email');
      if (outcome.status === 'revealed' || outcome.status === 'already_revealed') {
        tally.found += 1;
        const credits = typeof outcome.credits === 'number' ? outcome.credits : 0;
        tally.creditsMicros += Math.round(credits * MICROS_PER_CREDIT);
      } else if (outcome.status === 'not_found') {
        tally.missed += 1;
      } else if (outcome.reason === 'insufficient_credits') {
        // Refused when the charge settled, after the batch's upfront check passed.
        tally.insufficient = true;
      } else if (outcome.reason === 'spend_limit_exceeded') {
        tally.spendLimited = true;
      } else {
        tally.failed += 1;
      }
    } else if (item.error?.code === ENRICH_INSUFFICIENT_CREDITS) {
      tally.insufficient = true;
    } else if (item.error?.code === ENRICH_SPEND_LIMIT_EXCEEDED) {
      tally.spendLimited = true;
    } else {
      tally.failed += 1;
    }
  }
  return tally;
}

function mergeTallies(a: BatchRevealTally, b: BatchRevealTally): BatchRevealTally {
  return {
    found: a.found + b.found,
    missed: a.missed + b.missed,
    failed: a.failed + b.failed,
    creditsMicros: a.creditsMicros + b.creditsMicros,
    insufficient: a.insufficient || b.insufficient,
    spendLimited: a.spendLimited || b.spendLimited,
  };
}

export function batchRevealSummary(tally: BatchRevealTally): string {
  const found = tally.found === 1 ? '1 email' : `${tally.found} emails`;
  const head =
    tally.found > 0
      ? `Found ${found} · ${formatCredits(tally.creditsMicros)}.`
      : 'No emails found. You were not charged.';
  const missed =
    tally.found > 0 && tally.missed > 0 ? ` ${tally.missed} not found, not charged.` : '';
  const failed = tally.failed > 0 ? ` ${tally.failed} could not be checked.` : '';
  return `${head}${missed}${failed}`;
}

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export function readSearchResult(body: unknown): {
  found: number;
  newCount: number | null;
  microcredits: number | null;
} {
  const read = body as {
    data?: unknown;
    metadata?: { microcredits?: unknown; new_results?: unknown };
  } | null;
  return {
    found: Array.isArray(read?.data) ? read.data.length : 0,
    newCount: count(read?.metadata?.new_results),
    microcredits: count(read?.metadata?.microcredits),
  };
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function toContactProfileView(profile: ApiContactReveals['profile']): ContactProfileView {
  const history = Array.isArray(profile?.employment_history) ? profile.employment_history : [];
  const details = (profile?.profile ?? null) as {
    education?: unknown;
    skills?: unknown;
    languages?: unknown;
    connections?: unknown;
  } | null;
  const education = details?.education;
  const connections = details?.connections;
  return {
    employment: history.map((raw) => {
      const role = (raw ?? {}) as Record<string, unknown>;
      return {
        title: text(role.title),
        firm_name: text(role.firm_name),
        location: text(role.location),
        start: text(role.start),
        end: text(role.end),
        description: text(role.description),
        contract_type: text(role.contract_type),
        current: role.current === true,
        firm_website: text(role.firm_website),
        firm_linkedin_id: text(role.firm_linkedin_id),
      };
    }),
    education: (Array.isArray(education) ? education : []).map((raw) => {
      const entry = (raw ?? {}) as Record<string, unknown>;
      return {
        school: text(entry.school),
        degree: text(entry.degree),
        fields_of_study: Array.isArray(entry.fields_of_study)
          ? entry.fields_of_study.filter((field): field is string => typeof field === 'string')
          : [],
        start: text(entry.start),
        end: text(entry.end),
      };
    }),
    skills: (Array.isArray(details?.skills) ? details.skills : []).flatMap((skill) => {
      const name = text(skill);
      return name ? [name] : [];
    }),
    languages: (Array.isArray(details?.languages) ? details.languages : []).flatMap((raw) => {
      const entry = (raw ?? {}) as Record<string, unknown>;
      const name = text(entry.name);
      return name ? [{ name, level: text(entry.level) }] : [];
    }),
    connections:
      typeof connections === 'number' && Number.isFinite(connections) && connections > 0
        ? connections
        : null,
  };
}

// -- Cache patching ------------------------------------------------------------

function swapReach<T extends { id: string } & ReachSource>(
  row: T,
  fresh: ReadonlyMap<string, ApiContactSummary>,
): T {
  const next = fresh.get(row.id);
  if (!next) return row;
  return {
    ...row,
    email: next.email,
    phone: next.phone,
    reveals: next.reveals ?? row.reveals,
    reach: next.reach ?? row.reach,
  };
}

export function appendContactIncludes(qs: URLSearchParams): URLSearchParams {
  for (const key of CONTACT_INCLUDES) qs.append('include', key);
  return qs;
}

/** A charge moved the balance: the Usage page's cached credits refetch. */
function markCreditsSpent(queryClient: QueryClient, workspaceId: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ['enrichment-credits', workspaceId] });
}

/** Re-reads these contacts and patches them into every cached list, contact drawer and Contacts tab. */
export async function refreshContactReach(
  queryClient: QueryClient,
  workspaceId: string,
  ids: readonly string[],
): Promise<void> {
  const inWorkspace = (query: { queryKey: readonly unknown[] }) =>
    query.queryKey[2] === workspaceId;
  const qs = appendContactIncludes(new URLSearchParams({ workspace_id: workspaceId }));
  const reads = await Promise.allSettled(
    chunkRecordIds(ids, BATCH_SIZE).map((chunk) =>
      apiFetch<{ contacts: ApiContactSummary[] }>(`/contacts/lookup?${qs.toString()}`, {
        method: 'POST',
        body: JSON.stringify({ ids: chunk }),
      }),
    ),
  );
  const fresh = new Map<string, ApiContactSummary>();
  for (const read of reads) {
    if (read.status === 'fulfilled') {
      for (const contact of read.value.contacts) fresh.set(contact.id, contact);
    }
  }
  // The charge already happened; what could not be re-read refetches rather than failing the reveal.
  const markStale = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['contacts-browse', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['contacts', workspaceId] }),
      queryClient.invalidateQueries({ queryKey: ['contact-detail'], predicate: inWorkspace }),
      queryClient.invalidateQueries({ queryKey: ['account-contacts'], predicate: inWorkspace }),
    ]);
  const failed = reads.some((read) => read.status === 'rejected');
  if (fresh.size > 0) patchContactReach(queryClient, workspaceId, fresh, inWorkspace);
  if (failed) await markStale();
}

function patchContactReach(
  queryClient: QueryClient,
  workspaceId: string,
  fresh: ReadonlyMap<string, ApiContactSummary>,
  inWorkspace: (query: { queryKey: readonly unknown[] }) => boolean,
): void {
  const patchList = (data: InfiniteData<ApiContactListResponse> | undefined) =>
    data && {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        contacts: page.contacts.map((contact) => swapReach(contact, fresh)),
      })),
    };

  queryClient.setQueriesData({ queryKey: ['contacts-browse', workspaceId] }, patchList);
  queryClient.setQueriesData({ queryKey: ['contacts', workspaceId] }, patchList);
  queryClient.setQueriesData<ApiContactDetailResponse>(
    { queryKey: ['contact-detail'], predicate: inWorkspace },
    (data) => data && { ...data, contact: swapReach(data.contact, fresh) },
  );
  // Only the Contacts tab's read; the drawer's plain read feeds the Details count and reports.
  queryClient.setQueriesData<ApiAccountContactsResponse>(
    {
      queryKey: ['account-contacts'],
      predicate: (query) => inWorkspace(query) && query.queryKey[3] === 'reach',
    },
    (data) => data && { ...data, contacts: data.contacts.map((row) => swapReach(row, fresh)) },
  );
}

// -- Queries -------------------------------------------------------------------

export function useEnrichStatus(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['enrich-status', workspaceId],
    enabled: Boolean(workspaceId),
    staleTime: STATUS_STALE_MS,
    queryFn: () =>
      apiFetch<ApiEnrichStatus>(
        `/enrich/status?workspace_id=${encodeURIComponent(workspaceId ?? '')}`,
      ),
  });
}

export function accountContactsReachPath(
  accountId: string,
  workspaceId: string,
  includeAgency: boolean,
): string {
  const qs = new URLSearchParams({
    workspace_id: workspaceId,
    limit: String(ACCOUNT_CONTACTS_LIMIT),
  });
  for (const key of ACCOUNT_CONTACT_INCLUDES) qs.append('include', key);
  qs.set('sort', 'persona');
  qs.set('exclude_agency', includeAgency ? 'false' : 'true');
  return `/accounts/${encodeURIComponent(accountId)}/contacts?${qs.toString()}`;
}

/** Flipping the agency switch keeps this firm's rows on screen; another firm or workspace never shows them. */
export function sameFirmPlaceholder(accountId: string | null, workspaceId: string | undefined) {
  return <T>(previous: T | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
    previousQuery?.queryKey[1] === accountId && previousQuery.queryKey[2] === workspaceId
      ? previous
      : undefined;
}

/** The account drawer's Contacts tab: reveals and reach, persona order, agency switch. */
export function useAccountContactsWithReach(
  accountId: string | null,
  workspaceId: string | undefined,
  includeAgency: boolean,
) {
  return useQuery({
    queryKey: ['account-contacts', accountId, workspaceId, 'reach', includeAgency],
    enabled: Boolean(accountId) && Boolean(workspaceId),
    placeholderData: sameFirmPlaceholder(accountId, workspaceId),
    queryFn: () =>
      apiFetch<ApiAccountContactsResponse>(
        accountContactsReachPath(accountId ?? '', workspaceId ?? '', includeAgency),
      ),
  });
}

// -- Mutations -----------------------------------------------------------------

function requireWorkspace(workspaceId: string | undefined): string {
  if (!workspaceId) throw new Error('A workspace is required for enrichment actions');
  return workspaceId;
}

export function useRevealContact(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      contactId: string;
      kind: ApiRevealKind;
    }): Promise<RevealResult> => {
      const ws = requireWorkspace(workspaceId);
      try {
        const body = await apiFetch<unknown>(
          `/enrich/contacts/${encodeURIComponent(input.contactId)}/reveal?workspace_id=${encodeURIComponent(ws)}`,
          { method: 'POST', body: JSON.stringify({ kinds: [input.kind] }) },
        );
        return readRevealResult(body, input.kind);
      } catch (error) {
        const refusal = creditRefusal(error);
        if (refusal) return refusal;
        throw error;
      }
    },
    onSuccess: async (result, input) => {
      if (!workspaceId) return;
      // A miss is remembered on the reach block, so a reload says so instead of offering it again.
      if (result.kind === 'miss') {
        await refreshContactReach(queryClient, workspaceId, [input.contactId]);
        return;
      }
      if (result.kind !== 'found') return;
      await Promise.all([
        refreshContactReach(queryClient, workspaceId, [input.contactId]),
        markCreditsSpent(queryClient, workspaceId),
        // A profile also files the free headline, about, location and role the detail read carries.
        input.kind === 'profile'
          ? queryClient.invalidateQueries({
              queryKey: ['contact-detail', input.contactId, workspaceId],
            })
          : null,
      ]);
    },
  });
}

export function useRevealContactsBatch(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: readonly string[] }): Promise<BatchRevealTally> => {
      const ws = requireWorkspace(workspaceId);
      const checked: string[] = [];
      const total = await sendRevealBatches(ws, input.ids, checked);
      // One re-read after every batch is sent, not one per batch; a miss is remembered on the reach block too.
      if (checked.length > 0) {
        await Promise.all([
          refreshContactReach(queryClient, ws, checked),
          total.found > 0 ? markCreditsSpent(queryClient, ws) : null,
        ]);
      }
      return total;
    },
  });
}

/** Sends 100-id batches until done or refused; collects the ids of every batch that ran. */
async function sendRevealBatches(
  ws: string,
  ids: readonly string[],
  checked: string[],
): Promise<BatchRevealTally> {
  let total = emptyTally();
  let sent = 0;
  for (const chunk of chunkRecordIds(ids, BATCH_SIZE)) {
    let tally: BatchRevealTally;
    try {
      tally = tallyBatchReveal(
        await apiFetch<unknown>(
          `/enrich/contacts/reveal/batch?workspace_id=${encodeURIComponent(ws)}`,
          { method: 'POST', body: JSON.stringify({ ids: chunk, kinds: ['email'] }) },
        ),
      );
    } catch (error) {
      if (apiErrorCode(error) === ENRICH_INSUFFICIENT_CREDITS) {
        tally = { ...emptyTally(), insufficient: true };
      } else if (apiErrorCode(error) === ENRICH_SPEND_LIMIT_EXCEEDED) {
        tally = { ...emptyTally(), spendLimited: true };
      } else if (sent === 0) {
        throw error;
      } else {
        // Earlier batches charged: report them and count the rest as unchecked.
        return mergeTallies(total, { ...emptyTally(), failed: ids.length - sent });
      }
    }
    sent += chunk.length;
    total = mergeTallies(total, tally);
    if (tally.found > 0 || tally.missed > 0) checked.push(...chunk);
    if (tally.insufficient || tally.spendLimited) break;
  }
  return total;
}

export function usePeopleSearch(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      accountId: string;
      families: readonly ApiRoleFamily[];
    }): Promise<
      | { kind: 'done'; found: number; newCount: number | null; microcredits: number | null }
      | { kind: 'insufficient_credits' }
      | { kind: 'spend_limit' }
    > => {
      const ws = requireWorkspace(workspaceId);
      try {
        const body = await apiFetch<unknown>(
          `/enrich/people/search?workspace_id=${encodeURIComponent(ws)}`,
          {
            method: 'POST',
            body: JSON.stringify({
              firms: [{ account_id: input.accountId }],
              role_families: input.families,
              limit: PEOPLE_SEARCH_LIMIT,
            }),
          },
        );
        return { kind: 'done', ...readSearchResult(body) };
      } catch (error) {
        const refusal = creditRefusal(error);
        if (refusal) return refusal;
        throw error;
      }
    },
    onSuccess: async (result, input) => {
      if (result.kind !== 'done') return;
      // The prefix covers the Contacts tab's read and the drawer's Details count; the page lists follow.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['account-contacts', input.accountId] }),
        ...(workspaceId
          ? ['contacts-browse', 'contacts', 'contact-browse-summary', 'enrichment-credits'].map(
              (root) => queryClient.invalidateQueries({ queryKey: [root, workspaceId] }),
            )
          : []),
      ]);
    },
  });
}
